import { useBuyingPower } from '@src/hooks/useBuyingPower';
import { useFeatureFlag } from '@src/hooks/useFeatureFlag';
import { ConnectionType, Flex, IndicatorBadge, useMarketAccountsContext, type Security } from '@talos/kyoko';
import { useCallback, useMemo } from 'react';
import { MarketAccountSelectionGrid } from '../../../../components/MarketAccountSelectionList/MarketAccountSelectionGrid';
import { MarketsHeader } from '../../../../components/MarketAccountSelectionList/styles';
import { useSecurityMarkets } from '../../../../hooks/useSecurityMarkets';
import { useDeepDiveContext } from '../../../../providers/DeepDiveProvider';

interface MarketsParams {
  security: Security;
}

export const Markets = ({ security }: MarketsParams) => {
  const securityMarkets = useMemo(() => security?.Markets || [], [security]);
  const { marketAccountsList } = useMarketAccountsContext();
  const {
    selectedMarkets,
    setSelectedMarkets,
    selectedExchangeMarkets,
    selectedMarketAccounts,
    setSelectedMarketAccounts,
  } = useDeepDiveContext();
  const { getSecurityMarkets } = useSecurityMarkets({
    markets: securityMarkets,
    showInactiveMarketAccounts: false,
  });
  const { canViewCreditAndBalances } = useFeatureFlag();

  const { buyingPowerByMarketAccount: baseBuyingPower } = useBuyingPower({
    symbol: security?.Symbol ?? '',
    currency: security?.BaseCurrency ?? '',
    threshold: '10s',
    tag: 'DeepDive/Markets',
    enabled: canViewCreditAndBalances,
  });

  const { buyingPowerByMarketAccount: quoteBuyingPower } = useBuyingPower({
    symbol: security?.Symbol ?? '',
    currency: security?.QuoteCurrency ?? '',
    threshold: '10s',
    tag: 'DeepDive/Markets',
    enabled: canViewCreditAndBalances,
  });

  const availableSecurityMarkets = useMemo(() => {
    return getSecurityMarkets(ConnectionType.MarketData);
  }, [getSecurityMarkets]);

  const securityMarketAccounts =
    marketAccountsList?.filter(ma => securityMarkets.includes(ma.Market)).map(ma => ma.Name) || [];

  const handleMarketsChange = useCallback(
    (newSelectedMarkets: string[]) => {
      setSelectedMarkets(newSelectedMarkets.toSorted());
    },
    [setSelectedMarkets]
  );

  const handleMarketAccountsChange = useCallback(
    (newSelectedMarketAccounts: string[]) => {
      setSelectedMarketAccounts(newSelectedMarketAccounts.toSorted());
    },
    [setSelectedMarketAccounts]
  );

  return (
    <Flex flexDirection="column" background="backgroundContent" flex={1}>
      <MarketsHeader>
        Markets
        <IndicatorBadge children={`${selectedExchangeMarkets.length} out of ${availableSecurityMarkets.length}`} />
      </MarketsHeader>
      <MarketAccountSelectionGrid
        baseBuyingPower={baseBuyingPower}
        quoteBuyingPower={quoteBuyingPower}
        availableMarkets={securityMarkets}
        availableMarketAccounts={securityMarketAccounts}
        selectedMarketAccounts={selectedMarketAccounts}
        selectedMarkets={selectedMarkets}
        security={security}
        scrollContent={true}
        onChangeMarketAccounts={handleMarketAccountsChange}
        onChangeMarkets={handleMarketsChange}
        connectionType={ConnectionType.MarketData}
        showInactiveMarketAccounts={false}
      />
    </Flex>
  );
};
